import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Brain, Search, Database, CheckCircle, Clock, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * EnhancedLoadingOverlay - Professional fullscreen loading overlay
 * Features:
 * - Smooth 15-second progress to 99%
 * - Waits for actual response completion
 * - Translated stages and messages
 * - Minimal but professional design
 * - Fullscreen with React Portal (proper z-index handling)
 * - 50% transparent background
 */
const EnhancedLoadingOverlay = ({
    stage = 'preparing',
    isComplete = false,
    onComplete = null,
    className = ''
}) => {
    const { t } = useTranslation();
    const [progress, setProgress] = useState(0);
    const [currentMessage, setCurrentMessage] = useState(0);

    // Define stages with icons and colors
    const stages = {
        preparing: {
            icon: Search,
            color: 'blue',
            translationKey: 'preparing'
        },
        generating: {
            icon: Brain,
            color: 'purple',
            translationKey: 'generating'
        },
        processing: {
            icon: Database,
            color: 'indigo',
            translationKey: 'processing'
        },
        finalizing: {
            icon: CheckCircle,
            color: 'green',
            translationKey: 'finalizing'
        },
        waiting: {
            icon: Clock,
            color: 'orange',
            translationKey: 'waiting'
        }
    };

    const currentStage = stages[stage] || stages.preparing;
    const StageIcon = currentStage.icon;

    // Rotating messages for variety
    const messages = [
        t('workshop4.operationalScenarios.aiGeneration.loadingMessages.generatingScenario'),
        t('workshop4.operationalScenarios.aiGeneration.loadingMessages.analyzingVulnerabilities'),
        t('workshop4.operationalScenarios.aiGeneration.loadingMessages.processingTechniques'),
        t('workshop4.operationalScenarios.aiGeneration.loadingMessages.almostDone')
    ];

    // Smooth progress animation to 99% over 20 seconds
    useEffect(() => {
        if (isComplete) {
            // Jump to 100% when complete
            setProgress(100);
            // Call onComplete after a brief delay for visual feedback
            const completeTimer = setTimeout(() => {
                if (onComplete) onComplete();
            }, 500);
            return () => clearTimeout(completeTimer);
        }

        // Calculate progress increment for 20 seconds to reach 99%
        const targetProgress = 99;
        const duration = 20000; // 20 seconds
        const intervalTime = 100; // Update every 100ms
        const totalSteps = duration / intervalTime;
        const increment = targetProgress / totalSteps;

        const progressInterval = setInterval(() => {
            setProgress(prev => {
                const next = prev + increment;
                // Cap at 99% until isComplete is true
                return next >= targetProgress ? targetProgress : next;
            });
        }, intervalTime);

        return () => clearInterval(progressInterval);
    }, [isComplete, onComplete]);

    // Rotate messages every 4 seconds
    useEffect(() => {
        const messageInterval = setInterval(() => {
            setCurrentMessage(prev => (prev + 1) % messages.length);
        }, 4000);

        return () => clearInterval(messageInterval);
    }, [messages.length]);

    // Use React Portal to render at document.body level for proper fullscreen
    return ReactDOM.createPortal(
        <div
            className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] ${className}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="loading-title"
            aria-describedby="loading-description"
        >
            <div className="relative max-w-lg w-full mx-4">
                {/* Animated background glow */}
                <div className={`absolute inset-0 bg-${currentStage.color}-500/20 blur-3xl rounded-full animate-pulse`} />

                {/* Main content card - Modern glassmorphism */}
                <div className="relative bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-10">
                    {/* Icon with animated ring */}
                    <div className="flex justify-center mb-8">
                        <div className="relative">
                            {/* Animated rings */}
                            <div className={`absolute inset-0 rounded-full bg-${currentStage.color}-500/30 animate-ping`} />
                            <div className={`absolute inset-0 rounded-full bg-${currentStage.color}-500/20 animate-pulse`} />

                            {/* Icon container */}
                            <div className={`relative p-6 rounded-full bg-gradient-to-br from-${currentStage.color}-500 to-${currentStage.color}-600 shadow-xl`}>
                                <StageIcon className="h-14 w-14 text-white" strokeWidth={2} />
                            </div>
                        </div>
                    </div>

                    {/* Stage title */}
                    <h3
                        id="loading-title"
                        className="text-3xl font-bold text-gray-900 dark:text-white text-center mb-3"
                    >
                        {t(`workshop4.operationalScenarios.aiGeneration.loadingStages.${currentStage.translationKey}.title`)}
                    </h3>

                    {/* Stage description */}
                    <p
                        id="loading-description"
                        className="text-gray-600 dark:text-gray-300 text-center mb-8 text-lg"
                    >
                        {t(`workshop4.operationalScenarios.aiGeneration.loadingStages.${currentStage.translationKey}.description`)}
                    </p>

                    {/* Progress bar */}
                    <div className="mb-6">
                        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden shadow-inner">
                            <div
                                className={`h-full bg-gradient-to-r from-${currentStage.color}-500 to-${currentStage.color}-400 transition-all duration-300 ease-out rounded-full relative shadow-lg`}
                                style={{ width: `${progress}%` }}
                            >
                                {/* Shimmer effect */}
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer" />
                            </div>
                        </div>

                        {/* Progress percentage and message */}
                        <div className="flex justify-between items-center mt-3">
                            <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                                {messages[currentMessage]}
                            </span>
                            <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                                {Math.round(progress)}%
                            </span>
                        </div>
                    </div>

                    {/* Additional info */}
                    <div className="flex items-center justify-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                        <Sparkles className="h-5 w-5 text-yellow-500 animate-pulse" />
                        <span>{t('workshop4.operationalScenarios.aiGeneration.loadingMessages.pleaseWait')}</span>
                    </div>
                </div>
            </div>

            {/* Custom shimmer animation */}
            <style jsx>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
        </div>,
        document.body // Render to document.body for proper fullscreen
    );
};

export default EnhancedLoadingOverlay;
